import { Component, Input, Output, EventEmitter, HostListener, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { filter, debounceTime } from 'rxjs/operators';
import {
  NotificationsApiService,
  Notification as AppNotification,
} from '../../../core/services/notifications/notifications-api.service';
import { NotificationStateService } from '../../../core/services/notifications/notification-state.service';

@Component({
  selector: 'app-notification-drawer',
  standalone: true, // standalone component
  imports: [CommonModule],
  templateUrl: './notification-drawer.component.html',
  styleUrls: ['./notification-drawer.component.css'],
})
export class NotificationDrawerComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();

  notifications: any[] = []; // Using any[] to map API response to view model if needed
  unreadCount = 0;
  isLoading = false;
  private pollingSubscription?: Subscription;
  private fcmNotificationSubscription?: Subscription;

  constructor(
    private notificationsService: NotificationsApiService,
    private notificationState: NotificationStateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // this.fetchNotifications();
    // this.fetchStats();

    // Subscribe to real-time FCM notifications
    this.fcmNotificationSubscription = this.notificationState.fcmNotification$
      .pipe(
        filter((fcmNotification) => fcmNotification !== null)
        // debounceTime(300)
      )
      .subscribe((fcmNotification) => {
        if (fcmNotification) {
          // Add FCM notification to the top of the list immediately
          const newNotification = {
            id: fcmNotification.messageId,
            title: fcmNotification.notification.title,
            message: fcmNotification.notification.body,
            type: fcmNotification.data.type || 'info',
            patient_id: fcmNotification.data.patient_id,
            read: false,
            is_read: false,
            time: 'Just now',
            created_at: new Date().toISOString(),
          };

          // De-duplicate: if we already have a notification with the same
          // type, patient and text, don't add another one
          const alreadyExists = this.notifications.some(
            (n) =>
              n.type === newNotification.type &&
              n.patient_id === newNotification.patient_id &&
              n.title === newNotification.title &&
              n.message === newNotification.message
          );

          if (!alreadyExists) {
            // Add to the beginning of the array
            this.notifications.unshift(newNotification);
            this.unreadCount++;
          }

          // Also refresh from API to get server data
          // setTimeout(() => {
          //   this.fetchNotifications();
          //   this.fetchStats();
          // }, 1000);
        }
      });

    // Poll for notifications every 30 seconds
    // this.pollingSubscription = interval(30000).subscribe(() => {
    //   this.fetchStats();
    //   if (this.isOpen) {
    //     this.fetchNotifications();
    //   }
    // });
  }

  ngOnChanges(): void {
    document.body.style.overflow = this.isOpen ? 'hidden' : '';
    if (this.isOpen) {
      this.fetchNotifications(); // Refresh when opened
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.isOpen) {
      this.onClose();
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    if (this.fcmNotificationSubscription) {
      this.fcmNotificationSubscription.unsubscribe();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  fetchStats(): void {
    this.notificationsService.getNotificationStats().subscribe({
      next: (stats) => {
        this.unreadCount = stats.unread_count;
      },
    });
  }

  fetchNotifications(): void {
    this.isLoading = true;
    this.notificationsService.getNotifications(1, 20).subscribe({
      next: (response) => {
        //console.log(response);
        this.notifications = response.notifications.map((n) => ({
          ...n,
          read: n.is_read, // Map is_read to read for template compatibility
          time: this.getTimeAgo(n.created_at),
          type: n.type || 'info',
        }));
        this.unreadCount = response.unread_count;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  markAllAsRead(): void {
    this.notificationsService.markAllAsRead().subscribe({
      next: () => {
        this.notifications.forEach((n) => (n.read = true));
        this.unreadCount = 0;
      },
    });
  }

  clearAll(): void {
    // Implement if API supports it, otherwise just local clear or nothing
  }

  handleNotificationClick(notification: any): void {
    if (!notification.read) {
      this.notificationsService.markAsRead(notification.id).subscribe(() => {
        notification.read = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      });
    }

    // Navigation logic
    if (
      (notification.type === 'followup' || notification.type === 'followup_reminder') &&
      notification.patient_id
    ) {
      this.router.navigate(['/patient/patient-report'], {
        queryParams: { id: notification.patient_id },
      });
      this.onClose();
    }
  }

  getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  }
}
